import React, { useState, useEffect, useCallback } from 'react';
import Header from '../components/Header';
import UserProfileModal from '../components/UserProfileModal';
import UserRegistrationModal from '../components/UserRegistrationModal';
import { useAuth } from '../contexts/AuthContext';

interface User {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  role: 'administrator' | 'selger' | 'leder' | 'bruker';
  department: 'salg' | 'markedsfoering' | 'redaksjon' | 'administrasjon' | 'ledelse' | 'web';
  newspaper: string;
  isActive: boolean;
  lastLogin: string | null;
  createdAt: string;
  updatedAt: string;
}

interface UserStats {
  overview: {
    totalUsers: number;
    activeUsers: number;
    inactiveUsers: number;
    recentUsers: number;
  };
  distribution: {
    byRole: Array<{ _id: string; count: number }>;
    byDepartment: Array<{ _id: string; count: number }>;
  };
}

const UserManagement: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [stats, setStats] = useState<UserStats | null>(null);
  
  // Filters and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  const ITEMS_PER_PAGE = 10;

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: ITEMS_PER_PAGE.toString(),
      });

      if (searchTerm) params.append('search', searchTerm);
      if (departmentFilter) params.append('department', departmentFilter);
      if (roleFilter) params.append('role', roleFilter);
      if (activeFilter) params.append('isActive', activeFilter);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/users?${params}`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setTotalPages(data.pagination.totalPages);
        setTotalUsers(data.pagination.totalUsers);
        setError(null);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError(error instanceof Error ? error.message : 'Error loading users');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, departmentFilter, roleFilter, activeFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/stats/overview`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching user statistics:', error);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'administrator') {
      fetchUsers();
    }
  }, [fetchUsers, user?.role]);

  useEffect(() => {
    if (user?.role === 'administrator') {
      fetchStats();
    }
  }, [fetchStats, user?.role]);

  // Redirect if not admin (after hooks)
  if (user?.role !== 'administrator') {
    return (
      <div className="user-management-page">
        <Header />
        <div className="user-management-content">
          <div className="container">
            <h1>Ingen tilgang</h1>
            <p>Du har ikke tilgang til brukerstyring. Kun administratorer kan se denne siden.</p>
          </div>
        </div>
      </div>
    );
  }

  const handleUserClick = (clickedUser: User) => {
    setSelectedUser(clickedUser);
    setShowUserModal(true);
  };

  const handleUserUpdate = () => {
    // Refresh user list after update
    fetchUsers();
    fetchStats();
    setShowUserModal(false);
    setSelectedUser(null);
  };

  const handleRegistrationSuccess = () => {
    // Refresh user list and stats after new user registration
    fetchUsers();
    fetchStats();
    setShowRegistrationModal(false);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleFilterChange = (filterType: string, value: string) => {
    setCurrentPage(1); // Reset to first page when filtering
    
    switch (filterType) {
      case 'department':
        setDepartmentFilter(value);
        break;
      case 'role':
        setRoleFilter(value);
        break;
      case 'active':
        setActiveFilter(value);
        break;
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDepartmentFilter('');
    setRoleFilter('');
    setActiveFilter('');
    setCurrentPage(1);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Aldri';
    return new Date(dateString).toLocaleDateString('no-NO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'administrator': return 'badge badge-admin';
      case 'leder': return 'badge badge-manager';
      case 'selger': return 'badge badge-sales';
      default: return 'badge badge-user';
    }
  };

  const getDepartmentBadgeClass = (department: string) => {
    switch (department) {
      case 'administrasjon': return 'badge badge-admin';
      case 'ledelse': return 'badge badge-management';
      case 'salg': return 'badge badge-sales';
      case 'markedsfoering': return 'badge badge-marketing';
      case 'redaksjon': return 'badge badge-editorial';
      case 'web': return 'badge badge-web';
      default: return 'badge badge-default';
    }
  };

  return (
    <div className="user-management-page">
      <Header />
      
      <div className="user-management-content">
        <div className="container">
          <div className="user-management-header">
            <h1>Brukerstyring</h1>
            <p>Administrer brukere, roller og tilganger</p>
          </div>

          {/* Statistics Cards */}
          {stats && (
            <div className="stats-grid">
              <div className="stat-card">
                <h3>{stats.overview.totalUsers}</h3>
                <p>Totalt antall brukere</p>
              </div>
              <div className="stat-card">
                <h3>{stats.overview.activeUsers}</h3>
                <p>Aktive brukere</p>
              </div>
              <div className="stat-card">
                <h3>{stats.overview.inactiveUsers}</h3>
                <p>Inaktive brukere</p>
              </div>
              <div className="stat-card">
                <h3>{stats.overview.recentUsers}</h3>
                <p>Nye brukere (30 dager)</p>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="user-filters">
            <div className="search-section">
              <input
                type="text"
                placeholder="Søk etter brukernavn, navn eller avis..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="search-input"
              />
            </div>
            
            <div className="filter-section">
              <div className="filters-row">
                <select
                  value={departmentFilter}
                  onChange={(e) => handleFilterChange('department', e.target.value)}
                  className="filter-select"
                >
                  <option value="">Alle avdelinger</option>
                  <option value="salg">Salg</option>
                  <option value="markedsfoering">Markedsføring</option>
                  <option value="redaksjon">Redaksjon</option>
                  <option value="administrasjon">Administrasjon</option>
                  <option value="ledelse">Ledelse</option>
                  <option value="web">Web</option>
                </select>
                
                <select
                  value={roleFilter}
                  onChange={(e) => handleFilterChange('role', e.target.value)}
                  className="filter-select"
                >
                  <option value="">Alle roller</option>
                  <option value="administrator">Administrator</option>
                  <option value="leder">Leder</option>
                  <option value="selger">Selger</option>
                  <option value="bruker">Bruker</option>
                </select>
                
                <select
                  value={activeFilter}
                  onChange={(e) => handleFilterChange('active', e.target.value)}
                  className="filter-select"
                >
                  <option value="">Alle statuser</option>
                  <option value="true">Aktive</option>
                  <option value="false">Inaktive</option>
                </select>
              </div>
              
              <div className="filter-actions">
                <button onClick={clearFilters} className="btn btn-secondary">
                  Nullstill filtre
                </button>
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="users-section">
            <div className="section-header">
              <div className="section-title">
                <h2>Brukere ({totalUsers})</h2>
              </div>
              <div className="section-actions">
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowRegistrationModal(true)}
                  disabled={loading}
                >
                  👤 Registrer bruker
                </button>
              </div>
            </div>

            {loading && (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Laster brukere...</p>
              </div>
            )}

            {error && (
              <div className="error-state">
                <p className="error-message">⚠️ {error}</p>
                <button onClick={fetchUsers} className="btn btn-primary">
                  Prøv igjen
                </button>
              </div>
            )}

            {!loading && !error && users.length > 0 && (
              <div className="users-table-container">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Brukernavn</th>
                      <th>Navn</th>
                      <th>E-post</th>
                      <th>Telefon</th>
                      <th>Rolle</th>
                      <th>Avdeling</th>
                      <th>Avis</th>
                      <th>Status</th>
                      <th>Sist innlogget</th>
                      <th>Opprettet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((userData) => (
                      <tr key={userData.id} className="user-row">
                        <td>
                          <button
                            className="username-link"
                            onClick={() => handleUserClick(userData)}
                          >
                            {userData.username}
                          </button>
                        </td>
                        <td>{userData.firstName} {userData.lastName}</td>
                        <td className="email-cell">{userData.email}</td>
                        <td className="phone-cell">{userData.phone || '-'}</td>
                        <td>
                          <span className={getRoleBadgeClass(userData.role)}>
                            {userData.role}
                          </span>
                        </td>
                        <td>
                          <span className={getDepartmentBadgeClass(userData.department)}>
                            {userData.department}
                          </span>
                        </td>
                        <td>{userData.newspaper}</td>
                        <td>
                          <span className={`status-badge ${userData.isActive ? 'active' : 'inactive'}`}>
                            {userData.isActive ? 'Aktiv' : 'Inaktiv'}
                          </span>
                        </td>
                        <td>{formatDate(userData.lastLogin)}</td>
                        <td>{formatDate(userData.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && !error && users.length === 0 && (
              <div className="empty-state">
                <p>Ingen brukere funnet med gjeldende filtre.</p>
                <button onClick={clearFilters} className="btn btn-secondary">
                  Nullstill filtre
                </button>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="btn btn-secondary"
                >
                  Forrige
                </button>
                
                <span className="pagination-info">
                  Side {currentPage} av {totalPages}
                </span>
                
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="btn btn-secondary"
                >
                  Neste
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* User Profile Modal */}
      {showUserModal && selectedUser && (
        <UserProfileModal
          user={selectedUser}
          isOpen={showUserModal}
          onClose={() => {
            setShowUserModal(false);
            setSelectedUser(null);
          }}
          onUpdate={handleUserUpdate}
        />
      )}

      {/* User Registration Modal */}
      <UserRegistrationModal
        isOpen={showRegistrationModal}
        onClose={() => setShowRegistrationModal(false)}
        onSuccess={handleRegistrationSuccess}
      />
    </div>
  );
};

export default UserManagement;